import React, { useState, useEffect, useMemo } from 'react';
import {
    Box, Button, IconButton, Chip, Stack, Typography, Grid,
    Card, Divider, CardContent, CardHeader, Autocomplete, TextField, Modal, Alert,
    Checkbox, FormControlLabel, Switch
} from '@mui/material';
import { AddCircle, CheckCircle, Delete, HelpOutline } from '@mui/icons-material';

const AnalysisConditions = ({ value, onChange, analysisData }) => {
    const [analysis, setAnalysis] = useState(value.analysis || []);
    const [analysisType, setAnalysisType] = useState(null);
    const [detail, setDetail] = useState('');
    const [allValues, setAllValues] = useState(false);
    const [limitCumulate, setLimitCumulate] = useState(false);
    const [modalData, setModalData] = useState(null);
    const [error, setError] = useState('');

    useEffect(() => {
        setAnalysis(value.analysis || []);
    }, [value.analysis]);

    useEffect(() => {
        onChange({ analysis });
    }, [analysis, onChange]);

    useEffect(() => {
        if (allValues) {
            setDetail('*');
            return;
        }

        if (detail === '*') {
            setDetail('');
        }
    }, [allValues]);

    const handleAddAnalysis = () => {
        const normalizedDetail = allValues ? '*' : detail.trim();

        if (analysisType && normalizedDetail) {
            setAnalysis([
                ...analysis,
                {
                    analysisData: analysisType,
                    detail: normalizedDetail,
                    limit_cumulate: limitCumulate,
                }
            ]);
            setAnalysisType(null);
            setDetail('');
            setAllValues(false);
            setLimitCumulate(false);
            setError('');
        } else {
            setError('Please select type and enter detail');
        }
    };

    const handleDelete = (index) => {
        setAnalysis(analysis.filter((_, i) => i !== index));
    };

    const openModal = (data) => {
        setModalData(data);
    };

    const closeModal = () => {
        setModalData(null);
    };

    const displayItems = useMemo(() => analysis, [analysis]);

    return (
        <Card variant="outlined">
            <CardHeader title="Analysis Conditions" />
            <CardContent>
                {error && <Alert severity="error">{error}</Alert>}
                <Divider sx={{ my: 2 }} />

                <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
                    {displayItems.map((item, index) => (
                        <Chip
                            key={index}
                            label={`${item.analysisData.name}(${item.analysisData.model}):[${item.analysisData.class_name}] with value ${item.detail} ${item.limit_cumulate ? '(Limit cumulate)' : ''}`}
                            onDelete={() => handleDelete(index)}
                            onClick={() => openModal(item.analysisData)}
                            deleteIcon={<Delete />}
                            variant="outlined"
                            color="primary"
                        />
                    ))}
                </Stack>

                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={allValues ? 7 : 5}>
                        <Autocomplete
                            size="small"
                            options={analysisData}
                            getOptionLabel={(option) => `${option.name}(${option.model}) [${option.class_name}]`}
                            value={analysisType}
                            onChange={(_, newValue) => setAnalysisType(newValue)}
                            renderInput={(params) => <TextField {...params} label="Analysis Type" />}
                        />
                    </Grid>

                    {!allValues && (
                        <Grid item xs={12} md={2}>
                            <TextField
                                label="Value"
                                size="small"
                                value={detail}
                                onChange={(e) => setDetail(e.target.value)}
                                helperText="Use * for all values."
                                fullWidth
                            />
                        </Grid>
                    )}

                    <Grid item xs={12} md={4}>
                        <FormControlLabel
                            control={(
                                <Switch
                                    checked={allValues}
                                    onChange={(event) => setAllValues(event.target.checked)}
                                />
                            )}
                            label="All values (*)"
                        />
                        <FormControlLabel
                            control={(
                                <Checkbox
                                    checked={limitCumulate}
                                    onChange={(event) => setLimitCumulate(event.target.checked)}
                                    size="small"
                                />
                            )}
                            label="Limit cumulate"
                        />
                    </Grid>

                    <Grid item xs={12} md={1} display="flex" alignItems="center" gap={1} justifyContent="flex-end">
                        {analysisType && (analysisType.hint || analysisType.hint_file_url) && (
                            <IconButton color="info" onClick={() => openModal(analysisType)} title={analysisType.hint}>
                                <HelpOutline />
                            </IconButton>
                        )}
                        <IconButton onClick={handleAddAnalysis} color="primary">
                            <CheckCircle />
                        </IconButton>
                    </Grid>
                </Grid>

                {modalData && (
                    <Modal open onClose={closeModal}>
                        <Box sx={{
                            position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                            bgcolor: 'background.paper', border: '1px solid #ccc', borderRadius: 2, p: 2, maxWidth: 400
                        }}>
                            <Typography variant="h6">Hint: {modalData.name}</Typography>
                            {modalData.hint && <Typography variant="body2" sx={{ mt: 1 }}>{modalData.hint}</Typography>}
                            {modalData.hint_file_url && (
                                <Box sx={{ mt: 1 }}>
                                    <img src={modalData.hint_file_url} alt="Hint" style={{ width: '100%', borderRadius: 4 }} />
                                </Box>
                            )}
                            <Button sx={{ mt: 2 }} onClick={closeModal}>Close</Button>
                        </Box>
                    </Modal>
                )}
            </CardContent>
        </Card>
    );
};

export default AnalysisConditions;
